/* eslint-disable camelcase, max-lines */
/**
 * Source of truth for metadata-manager schemas.
 *
 * Each field descriptor is standard JSON Schema (the draft-07 vocabulary
 * understood by ajv and json-schema-to-ts) plus `x-` extension keywords, so
 * the same object serves three jobs:
 * - derives the GlobalMetadata type below via FromSchema
 * - validates values at runtime with ajv (see utils/metadata-schema.utils.ts)
 * - drives the MetadataManagementTool UI: `description` is help text,
 *   `examples[0]` is the input placeholder, `enum` renders a select
 *
 * Conventions:
 * - Schema keys are the canonical camelCase names for a field, derived from
 *   `x-rawKey` (transformMeta camelCases raw Ticketure keys on read). A key
 *   may drop the audit's `<scope>:<consumer>:` namespace — `seoTitle` for
 *   `slr:tkt:seo_title` — but it may never name anything else. Note the name
 *   the APP reads keeps the namespace (`slrTktSeoTitle`), which is what the
 *   constants in config/ticketure-metadata.config.ts hold.
 * - `x-rawKey` records the key as actually stored in Ticketure, and is REQUIRED
 *   on every `x-source: 'ticketure'` field — writes must use it, never the
 *   camelCase name. See TicketureFieldSchema below for why an absent raw key
 *   silently corrupts data rather than failing.
 * - `x-status` carries field-maturity tags (see META_STATUSES) so caveats
 *   that used to live only in code comments are data the docs generator and
 *   the management tool can surface. Absent means live. The tags:
 *     - `cutover-pending`: the app reads the meta first but falls back to
 *       the field's legacy home (generated site configs, messages files) when
 *       the key is unset (utils/data-access/seller-fallbacks.ts), so a blank
 *       value is not really blank. Drop the tag once the meta is the only
 *       source.
 *     - `not-read`: nothing in the app reads this field yet (it reads a
 *       hardcoded constant, env var or site config instead), so editing it has
 *       no effect. The management tool hides it and the docs list it as not
 *       live. Every `x-source: 'vercel'` field carries it until the app reads
 *       Edge Config. Drop the tag in the change that makes the app read it.
 * - `x-proposedKey` is the future Ticketure key proposed by the
 *   metadata-naming audit (`<scope>:<consumer>:<name>`, e.g.
 *   `slr:tkt:has_up_front_fee`). Not live yet — reads/writes keep using
 *   `x-rawKey` until the Ticketure data is migrated.
 * - `x-source` routes reads/writes: ticketure meta, vercel edge config, or
 *   airtable.
 * - `type` describes the PARSED value; Ticketure wire values are always
 *   strings and are coerced before validation ('1'/'true' style flags become
 *   booleans, numeric strings become numbers, json blobs are JSON.parsed).
 * - `examples` is MANDATORY. The tool must be able to show a worked example
 *   for any field an operator has left blank or filled in wrongly, so a field
 *   without one is a field the tool cannot help with. `examples[0]` doubles as
 *   the input placeholder, and every example is asserted valid against its own
 *   field schema (see metadata-schemas.config.spec.ts).
 * - There is ONE schema and every seller gets it — no per-seller additions or
 *   overrides. A field only one seller populates is just an optional field the
 *   others leave unset. Overrides were dropped deliberately: an override
 *   replaced the whole field descriptor, so forgetting to restate `x-rawKey` or
 *   `x-requiredFor` silently redirected that seller's writes to the wrong
 *   Ticketure key and dropped the field from its go-live checklist.
 * - `x-appliesTo` vs `x-requiredFor` answer different questions (do not conflate):
 *     - `x-appliesTo` (REQUIRED on every field) = visibility / relevance.
 *       "Which seller types should ever see or set this field?" Filters the
 *       management tool (admission → exhibition, events → events). Events-only
 *       fields never appear on admission. Use `SELLER_TYPES` for both sides.
 *     - `x-requiredFor` (optional) = go-live completeness.
 *       "Which seller types must have a non-blank value before go-live?"
 *       Absent means optional. Drives the missing-required checklist only.
 *   A field can apply to a side without being required there (optional but
 *   side-specific). `x-requiredFor` must be a subset of `x-appliesTo`.
 */
import type { FromSchema, JSONSchema } from 'json-schema-to-ts'

export const META_SOURCES = ['ticketure', 'vercel'] as const

export type MetaSource = typeof META_SOURCES[number]

/** The two kinds of Ticketure seller a venue has; see `x-appliesTo`/`x-requiredFor` below */
export const SELLER_TYPES = ['exhibition', 'events'] as const

export type SellerType = typeof SELLER_TYPES[number]

/**
 * Field-maturity tags carried by `x-status`; see the file header. Absent
 * means live: the app reads the field from the source modeled here.
 */
export const META_STATUSES = ['cutover-pending', 'not-read'] as const

export type MetaStatus = typeof META_STATUSES[number]

/**
 * Reusable JSON Schema `pattern` values.
 *
 * Only applied to NEW keys: the live values behind the legacy keys have not
 * been audited, and a pattern that rejects data already sitting in Ticketure
 * would break the management tool for existing sellers. Add one to a legacy
 * key only after auditing that key's live values.
 */
export const META_PATTERNS = {
  /** Absolute http(s) URL — for links and API bases, which must resolve anywhere */
  url: '^https?://\\S+$',
  /** Absolute http(s) URL or a root-relative path — asset fields accept either a hosted URL or a repo asset */
  urlOrPath: '^(?:https?://|/)\\S+$',
  /** Lowercase kebab-case route segment */
  slug: '^[a-z0-9]+(?:-[a-z0-9]+)*$',
  /** Six-digit hex color */
  hexColor: '^#[0-9a-fA-F]{6}$',
  /** Uppercase venue code */
  siteCode: '^[A-Z][A-Z0-9_]{1,15}$',
  /** Two-letter US state code */
  stateCode: '^[A-Z]{2}$',
  /** RFC 4122 UUID (any version), lowercase or uppercase */
  uuid: '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$',
} as const

/**
 * The JSON Schema half of a descriptor: everything ajv validates a value
 * against. Recursive, so `object` and `array` fields can describe the inside
 * of their blob rather than bottoming out at "is it parseable JSON".
 */
export type MetaValueSchema = {
  /** JSON Schema type of the parsed value */
  type: 'string' | 'number' | 'boolean' | 'object' | 'array'
  /** Restrict the value to a fixed set */
  enum?: readonly string[]
  /** Regex the (string) value must match; ajv enforces it, see META_PATTERNS */
  pattern?: string
  /** `object` values: the shape of the parsed blob */
  properties?: Readonly<Record<string, MetaValueSchema>>
  /** `object` values: the properties the blob must carry to be usable */
  required?: readonly string[]
  /** `array` values: the shape of each element */
  items?: MetaValueSchema
  /** Help text; on a nested node the tool shows it against the sub-input */
  description?: string
}

/** An instance of a field's parsed value, as carried in `examples` */
export type MetaExample =
  | string
  | number
  | boolean
  | readonly unknown[]
  | Readonly<Record<string, unknown>>

/** The half of a field descriptor that does not depend on where the value lives */
type MetaFieldBase = MetaValueSchema & {
  description: string
  /**
   * JSON Schema examples: instances of the PARSED value (so an `object` field's
   * example is a blob, not a JSON string). Required — see the note on
   * `examples` in the file header. The first doubles as the input placeholder.
   */
  examples: readonly MetaExample[]
  /** Proposed future Ticketure key (`<scope>:<consumer>:<name>`) from the metadata-naming audit; not yet live */
  'x-proposedKey'?: string
  /** Field-maturity tags (see META_STATUSES and the file header); absent means live */
  'x-status'?: readonly MetaStatus[]
  /**
   * GO-LIVE COMPLETENESS (not visibility).
   *
   * Seller types that must have a non-blank value before the resource can go
   * live — the seller itself for `seller` fields, one event for an
   * `event_template` field. Absent means the field is optional for every side
   * it applies to. Drives the missing-required checklist
   * (getMissingRequiredFields / the panel summary) — see
   * utils/metadata-schema.utils.ts.
   *
   * Different from `x-appliesTo`: a field can show on exhibition without being
   * required there. When set, every entry must also appear in `x-appliesTo`
   * (you cannot require a field for a seller type it does not apply to).
   */
  'x-requiredFor'?: readonly SellerType[]
  /**
   * VISIBILITY / RELEVANCE (not go-live completeness).
   *
   * Seller types this field is relevant for. The metadata tool filters the form
   * by the current seller type (admission → exhibition, events → events), so a
   * field with `x-appliesTo: ['events']` never appears on the admission panel —
   * including optional events-only fields that have no `x-requiredFor`.
   * Required on every field — use `SELLER_TYPES` when both sides need it.
   *
   * Different from `x-requiredFor`: applies = "should this input exist for this
   * seller type?"; requiredFor = "must it be filled before go-live?". Optional
   * side-specific fields have appliesTo set and requiredFor absent.
   */
  'x-appliesTo': readonly SellerType[]
}

/**
 * A field whose value lives in Ticketure meta.
 *
 * `x-rawKey` is REQUIRED, and that is load-bearing rather than pedantic.
 * transformMeta camelCases raw keys on read with lodash, which splits on `:`
 * and `_` alike — so `slr:tkt:tax_waived_enabled` and a literal key
 * `slrTktTaxWaivedEnabled` BOTH read back as `slrTktTaxWaivedEnabled`. If a
 * field carried no raw key, the write path would fall back to the camelCase
 * schema key and store a SECOND row in Ticketure alongside the real one. Both
 * rows then transform to the same name, transformMetaData emits both, and
 * whichever `.find()` reaches first wins — so an operator's edit appears to
 * save and then intermittently does nothing, with no error and no log.
 *
 * The invariant that keeps the write key and the read key in agreement is that
 * the schema key is `camelCase(x-rawKey)` with the `<scope>:<consumer>:`
 * namespace either kept or dropped, and that no two raw keys on one resource
 * camelCase to the same name; metadata-schemas.config.spec.ts asserts both for
 * every field.
 */
type TicketureFieldSchema = MetaFieldBase & {
  'x-source': 'ticketure'
  /** Raw key as stored in Ticketure; writes MUST use this, never the camelCase name */
  'x-rawKey': string
}

/**
 * A field whose value lives in the Vercel Edge Config (or, in future, Airtable).
 * There is no raw key: the schema key IS the storage key, because the Edge
 * Config payload is built from these keys directly (see the edge-config script).
 */
type ManagedFieldSchema = MetaFieldBase & {
  'x-source': Exclude<MetaSource, 'ticketure'>
  'x-rawKey'?: never
}

/** A metadata field descriptor: JSON Schema plus x- extension keywords */
export type MetaFieldSchema = TicketureFieldSchema | ManagedFieldSchema

export type MetaEntityKind =
  // Ticketure resources
  'seller' | 'event_template' | 'ticket_group' | 'ticket_type'
  // Edge Config globals (resourceId: 'global' at runtime)
  | 'feature-flags' | 'app-config'

type EntitySchemaMap = Record<string, MetaFieldSchema>

type SchemasShape = {
  global: Record<MetaEntityKind, EntitySchemaMap>
}

export const METADATA_SCHEMAS = {
  /**
   * The one schema. Every seller gets exactly this — there are no per-seller
   * additions or overrides, so onboarding a seller never touches this file.
   *
   * Grouped by what the metadata ATTACHES TO (the tool's `resourceType`),
   * not by where it is stored — storage is routed per field via `x-source`:
   * - `seller` / `event_template` / `ticket_group` / `ticket_type` are
   *   Ticketure resources; each entry describes a meta key on one of those.
   * - `feature_flag` / `app_config` are app-wide and live
   *   in the Vercel Edge Config (`resourceId: 'global'` at runtime).
   *
   * Fields only one seller populates (Houston's tax-waived messaging, the
   * PREVIEW tier cap) still live here, simply left unset by everyone else. A
   * field is `x-requiredFor` a seller type or it is optional; "optional" is the
   * whole mechanism for go-live completeness. Which seller types *see* a field
   * in the management tool is `x-appliesTo` (required on every field) — the
   * renderer filters by the page's seller type (admission → exhibition).
   */
  global: {
    // Ticketure Specific resources
    // SELLER_META
    seller: {
      logoConfirmation: {
        type: 'string',
        description: 'Logo URL shown on the purchase confirmation page; falls back to the default logo',
        examples: ['https://cdn.meowwolf.com/logos/meow-wolf-confirmation.svg'],
        'x-source': 'ticketure',
        'x-rawKey': 'logo_confirmation',
        'x-proposedKey': 'slr:tkt:logo_confirmation',
        'x-appliesTo': SELLER_TYPES,
        'x-requiredFor': SELLER_TYPES,
      },
      calendarBanner: {
        type: 'string',
        description: 'Banner text rendered above the calendar',
        examples: ['Timed entry required — plan to arrive 15 minutes before your slot.'],
        'x-source': 'ticketure',
        'x-rawKey': 'calendar_banner',
        'x-proposedKey': 'slr:tkt:calendar_banner',
        'x-appliesTo': ['exhibition'],
      },
      // Required: unset reads as -1 (getMaxTicketsPerOrder), and OrderTickets
      // only caps when the value is > 0 — i.e. no value means no cap at all
      maxTicketsForPurchase: {
        type: 'number',
        description: 'Maximum tickets allowed in a single purchase',
        examples: [30],
        'x-source': 'ticketure',
        'x-rawKey': 'max_tickets_for_purchase',
        'x-proposedKey': 'slr:tkt:max_tickets_for_purchase',
        'x-appliesTo': SELLER_TYPES,
      },
      eventDetailsSellerAlert: {
        type: 'string',
        description: 'Alert text shown on the event details page',
        examples: ['Convergence Station closes early on July 4.'],
        'x-source': 'ticketure',
        'x-rawKey': 'event_details_seller_alert',
        // NOTE: the audit groups this under Event, but the app reads it from seller meta
        'x-proposedKey': 'evt:tkt:event_details_seller_alert',
        'x-appliesTo': SELLER_TYPES,
      },
      // Shape mirrors Transformed.SlideDatum[] — note this is an ARRAY, not an
      // object. getSlideData JSON.parses it and hands it straight to
      // ImageSlider, so a malformed entry renders a broken image with no alt
      // text rather than failing loudly.
      slider: {
        type: 'array',
        description: 'Slider content for the seller landing/info area',
        items: {
          type: 'object',
          properties: {
            img: { type: 'string', description: 'Slide image URL' },
            alt: { type: 'string', description: 'Alt text for the slide image' },
          },
          required: ['img', 'alt'],
        },
        examples: [[
          {
            img: 'https://cdn.meowwolf.com/slides/convergence-station-lobby.jpg',
            alt: 'Visitors entering the Convergence Station lobby',
          },
          {
            img: 'https://cdn.meowwolf.com/slides/convergence-station-portal.jpg',
            alt: 'A glowing portal inside the exhibition',
          },
        ]],
        'x-source': 'ticketure',
        'x-rawKey': 'slider',
        'x-proposedKey': 'slr:tkt:slider',
        'x-requiredFor': ['exhibition'],
        'x-appliesTo': SELLER_TYPES,
      },
      // `required` mirrors the runtime guards in utils/data-access/seller.utils.ts:
      // instanceOfPricingData drops the WHOLE block when title/subtitle/sections
      // is missing, and instanceOfPricingSection silently filters out any section
      // missing one of its six keys. Both failures are invisible on the page
      // today — the schema is what lets the tool name them.
      pricing: {
        type: 'object',
        description: 'Pricing-information block (title, rows) rendered on exhibition pages',
        properties: {
          title: { type: 'string', description: 'Heading for the pricing block' },
          subtitle: { type: 'string', description: 'Copy shown under the heading' },
          sections: {
            type: 'array',
            description: 'One row per price tier',
            items: {
              type: 'object',
              properties: {
                renderSection: { type: 'boolean', description: 'Whether the row is shown' },
                title: { type: 'string', description: 'Row title, e.g. the tier name' },
                description: { type: 'string', description: 'Row copy, e.g. eligibility' },
                price: { type: 'string', description: 'Display price, formatted' },
                discountedPrice: { type: 'string', description: 'Display price when discounted; empty string when none' },
                isLocal: { type: 'boolean', description: 'Row is the local-resident tier' },
                specialItem: {
                  type: 'string',
                  enum: ['annual-pass'],
                  description: 'Marks the row as a special (non-ticket) item',
                },
                cta: { type: 'string', description: 'Call-to-action label' },
                ctaLink: { type: 'string', description: 'Call-to-action URL' },
              },
              required: ['renderSection', 'title', 'description', 'price', 'discountedPrice', 'isLocal'],
            },
          },
        },
        required: ['title', 'subtitle', 'sections'],
        examples: [{
          title: 'Pricing',
          subtitle: 'Tickets are timed entry and priced by date.',
          sections: [
            {
              renderSection: true,
              title: 'Adult',
              description: 'Ages 14 and up',
              price: '$50',
              discountedPrice: '$45',
              isLocal: false,
            },
            {
              renderSection: true,
              title: 'Colorado Resident',
              description: 'Valid state ID required at entry',
              price: '$45',
              discountedPrice: '',
              isLocal: true,
            },
            {
              renderSection: true,
              title: 'Annual Pass',
              description: 'Unlimited visits for one year',
              price: '$125',
              discountedPrice: '',
              isLocal: false,
              specialItem: 'annual-pass',
              cta: 'Learn more',
              ctaLink: 'https://meowwolf.com/membership',
            },
          ],
        }],
        'x-source': 'ticketure',
        'x-rawKey': 'pricing',
        'x-proposedKey': 'slr:tkt:pricing',
        'x-appliesTo': ['exhibition'],
        'x-requiredFor': ['exhibition'],
      },
      // `required` mirrors the runtime guards in utils/data-access/seller.utils.ts:
      // isValidExhibitionInfoData drops the whole block without header/sections,
      // isValidExhibitionInfoSection filters out sections missing id/header/
      // renderSection. Same silent-failure story as `pricing` above.
      exhibitionInformation: {
        type: 'object',
        description: 'Exhibition information block rendered on exhibition pages',
        properties: {
          header: { type: 'string', description: 'Heading for the information block' },
          sections: {
            type: 'array',
            description: 'One entry per information section',
            items: {
              type: 'object',
              properties: {
                id: { type: 'string', description: 'Stable section id, unique within the block' },
                header: { type: 'string', description: 'Section heading' },
                renderSection: { type: 'boolean', description: 'Whether the section is shown' },
                address: { type: 'string', description: 'Postal address, for location sections' },
                mapsLink: { type: 'string', description: 'Map URL paired with the address' },
                content: { type: 'string', description: 'Section body copy' },
              },
              required: ['id', 'header', 'renderSection'],
            },
          },
        },
        required: ['header', 'sections'],
        examples: [{
          header: 'Visiting Convergence Station',
          sections: [
            {
              id: 'location',
              header: 'Location',
              renderSection: true,
              address: '1338 1st St, Denver, CO 80204',
              mapsLink: 'https://maps.google.com/?q=1338+1st+St+Denver+CO+80204',
            },
            {
              id: 'accessibility',
              header: 'Accessibility',
              renderSection: true,
              content: 'The exhibition is wheelchair accessible. Sensory guides are available at the front desk.',
            },
          ],
        }],
        'x-source': 'ticketure',
        'x-rawKey': 'exhibition_information',
        'x-proposedKey': 'slr:tkt:exhibition_information',
        'x-appliesTo': ['exhibition'],
        'x-requiredFor': ['exhibition'],
      },
      upFrontFee: {
        type: 'boolean',
        description: 'When true, fees are included in displayed ticket prices',
        examples: [true],
        'x-source': 'ticketure',
        'x-rawKey': 'up_front_fee',
        'x-proposedKey': 'slr:tkt:has_up_front_fee',
        'x-appliesTo': SELLER_TYPES,
      },
      // Not enabled for all sellers (off for LVS and PREVIEW), so it stays a
      // seller flag for now. Could instead be derived from
      // `hubspotNewsletterFormId` being set, once every seller that shows the
      // signup has a form id and the ones that don't, don't.
      enabledEventsNewsletter: {
        type: 'boolean',
        description: 'Shows the newsletter signup in the events header (uses the HubSpot ids)',
        examples: [true],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:enabled_events_newsletter',
        'x-appliesTo': ['events'],
        'x-status': ['cutover-pending'],
      },
      mwHeaderDescription: {
        type: 'string',
        description: 'Header description for the events listing page',
        examples: ['Live music, immersive art, and events all year long.'],
        'x-source': 'ticketure',
        'x-rawKey': 'mw:header_description',
        'x-proposedKey': 'slr:tkt:mw_header_description',
        'x-appliesTo': ['events'],
      },
      // The banner arm of the Portal Pass interrupt A/B test
      // (config/experiments.config.ts). Both fall back to the copy in
      // messages/modals.ts when unset, so leaving them blank is safe.
      portalPassBannerBody: {
        type: 'string',
        description: 'Portal-pass promo banner: full body copy, shown before the banner minimizes on scroll',
        examples: ['Upgrade to Annual Pass for $10+ / person and get a year of access to all exhibits'],
        'x-source': 'ticketure',
        'x-rawKey': 'portal_pass_banner_body',
        'x-proposedKey': 'slr:tkt:portalpass_banner_body',
        'x-appliesTo': ['exhibition'],
      },
      portalPassBannerBodyMinimized: {
        type: 'string',
        description: 'Portal-pass promo banner: condensed copy, shown once the banner minimizes on scroll',
        examples: ['Upgrade to Annual Pass for $10+ / person'],
        'x-source': 'ticketure',
        'x-rawKey': 'portal_pass_banner_body_minimized',
        'x-proposedKey': 'slr:tkt:portalpass_banner_body_minimized',
        'x-appliesTo': ['exhibition'],
      },
      portalPassEventUrl: {
        type: 'string',
        description: 'Portal-pass interrupt modal: event URL the confirm button links to',
        examples: ['https://tickets.meowwolf.com/santa-fe/admission/portal-pass'],
        'x-source': 'ticketure',
        'x-rawKey': 'portal_pass_event_url',
        'x-proposedKey': 'slr:tkt:portalpass_event_url',
        'x-appliesTo': ['exhibition'],
      },
      portalPassModalBody: {
        type: 'string',
        description: 'Portal-pass interrupt modal: body copy',
        examples: ['Your Portal Pass includes admission. Continue to reserve an entry time.'],
        'x-source': 'ticketure',
        'x-rawKey': 'portal_pass_modal_body',
        'x-proposedKey': 'slr:tkt:portalpass_modal_body',
        'x-appliesTo': ['exhibition'],
      },
      portalPassModalConfirmButton: {
        type: 'string',
        description: 'Portal-pass interrupt modal: confirm button label',
        examples: ['Use my Portal Pass'],
        'x-source': 'ticketure',
        'x-rawKey': 'portal_pass_modal_confirm_button',
        'x-proposedKey': 'slr:tkt:portalpass_modal_confirm_text',
        'x-appliesTo': ['exhibition'],
      },
      portalPassModalDismissButton: {
        type: 'string',
        description: 'Portal-pass interrupt modal: dismiss button label',
        examples: ['Buy tickets instead'],
        'x-source': 'ticketure',
        'x-rawKey': 'portal_pass_modal_dismiss_button',
        'x-proposedKey': 'slr:tkt:portalpass_modal_dismiss_text',
        'x-appliesTo': ['exhibition'],
      },
      taxWaivedEnabled: {
        type: 'boolean',
        description: 'Enables tax-waived messaging (Houston)',
        examples: [true],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:tax_waived_enabled',
        'x-appliesTo': SELLER_TYPES,
      },
      taxWaivedMsgNoTax: {
        type: 'string',
        description: 'Tax-waived message when no tax is charged',
        examples: ['No sales tax is charged on this purchase.'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:tax_waived_msg_no_tax',
        'x-appliesTo': SELLER_TYPES,
      },
      taxWaivedMsgWithTax: {
        type: 'string',
        description: 'Tax-waived message when tax is charged',
        examples: ['Sales tax is included in the ticket price.'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:tax_waived_msg_with_tax',
        'x-appliesTo': SELLER_TYPES,
      },
      processingFeeOverride: {
        type: 'string',
        description: 'Overrides the default ticket processing fee',
        examples: ['Fee in lieu of taxes'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:processing_fee_override',
        'x-appliesTo': SELLER_TYPES,
      },
      // NEW key (audit-convention from birth, no x-proposedKey). Replaces the
      // per-seller PERK100_CODE_ID_<SITE> env vars (config/seller.config.ts),
      // which feed the slug→id map in transformCartResponse.ts and the switch
      // in checkout.utils.ts getPerkCheckoutRuleId. The value is a Ticketure
      // checkout-rule id, so it belongs on the seller — and because Ticketure
      // dev and prod are separate instances with separate seller records, this
      // also dissolves the STAGING_ env split (each env's seller carries its
      // own id). Read-path cutover pending.
      perk100CheckoutRuleId: {
        type: 'string',
        description: 'Ticketure checkout-rule id applied for 100%-perk (member free-ticket) redemption',
        examples: ['017a07c0-d832-4a1b-9c6e-2b4d1f0a7c93'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:perk100_checkout_rule_id',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      // ── Modeled from the per-seller generated site configs (SiteEnv).
      // These are NEW Ticketure keys, named per the metadata-naming audit
      // from birth (hence no x-proposedKey). The app still READS them from
      // the generated site configs; the cutover to seller meta is a separate
      // change.
      hubspotNewsletterFormId: {
        type: 'string',
        description: 'HubSpot form id for the events newsletter signup',
        examples: ['9a1f0c2e-5f3b-4a7d-8c6e-2b4d1f0a7c93'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:hubspot_newsletter_form_id',
        'x-appliesTo': ['events'],
        'x-status': ['cutover-pending'],
        'x-requiredFor': ['events'],
      },
      hubspotPortalId: {
        type: 'string',
        description: 'HubSpot portal id for the events newsletter signup',
        examples: ['3425168'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:hubspot_portal_id',
        'x-appliesTo': ['events'],
        'x-status': ['cutover-pending'],
        'x-requiredFor': ['events'],
      },
      // ── Modeled from ThemeText / EventsThemeText (messages/sellers/*).
      // These are NEW Ticketure keys, named per the metadata-naming audit
      // from birth (hence no x-proposedKey). The app still READS the
      // hardcoded messages files; cutover to seller meta is a separate
      // change. Exhibition and events sellers are distinct Ticketure
      // sellers, so the events variant is these same keys written on the
      // events seller (plus slr:tkt:events_description below) — no separate
      // entity needed. meta.siteName is NOT modeled; the app composes it
      // from the org and city names.
      exhibitionName: {
        type: 'string',
        description: 'Exhibition name used in checkout copy, tracking, and the order summary',
        examples: ['The Real Unreal'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:exhibition_name',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      // The city the site is named for, which annual-pass membership roles
      // also name.
      // Already authored on nearly every seller under the unprefixed key.
      cityName: {
        type: 'string',
        description: 'City shown in the header, page titles and confirmation pages',
        examples: ['Santa Fe'],
        'x-source': 'ticketure',
        'x-rawKey': 'city_name',
        'x-proposedKey': 'slr:tkt:city_name',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      stateCode: {
        type: 'string',
        description: 'Two-letter state code shown on landing cards',
        examples: ['TX'],
        pattern: META_PATTERNS.stateCode,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:state_code',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      stateName: {
        type: 'string',
        description: 'Full state name used in SEO descriptions',
        examples: ['Texas'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:state_name',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      faqUrl: {
        type: 'string',
        description: 'FAQ page URL linked from the footer/help areas',
        examples: ['https://faq.meowwolf.com/concerts-events'],
        pattern: META_PATTERNS.url,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:faq_url',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      seoTitle: {
        type: 'string',
        description: 'SEO page title; when absent the app derives "<exhibition> | Tickets & Passes | <org> <city>" (exhibition) or "<org> <city> Events" (events)',
        examples: ['Convergence Station | Tickets & Passes | Meow Wolf Denver'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:seo_title',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      seoDescription: {
        type: 'string',
        description: 'SEO meta description for the seller’s pages',
        examples: ['Buy tickets to Convergence Station, Meow Wolf Denver’s four-story immersive art experience.'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:seo_description',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      seoShareTitle: {
        type: 'string',
        description: 'Social-share title; when absent the app derives it like seoTitle',
        examples: ['Meow Wolf Denver — Convergence Station'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:seo_share_title',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      seoShareImageUrl: {
        type: 'string',
        description: 'Social-share image; a hosted URL',
        examples: ['https://ticketmanager.meowwolf.com/v1/assets/meowwolf/6370f27b-d3d6-a55c-cb02-8b7191427d3b?sizex=1200'],
        pattern: META_PATTERNS.urlOrPath,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:seo_share_image_url',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
      },
      seoCanonicalUrl: {
        type: 'string',
        description: 'Canonical URL for the seller’s pages',
        examples: ['https://tickets.meowwolf.com/grapevine/admission/timed-tickets/'],
        pattern: META_PATTERNS.url,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:seo_canonical_url',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      newsletterOptinEvent: {
        type: 'string',
        description: 'Tracking event name fired when the newsletter opt-in is checked at checkout',
        examples: ['gv_subscribe_to_email_checkout'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:newsletter_optin_event',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      eventsDescription: {
        type: 'string',
        description: 'Intro copy for the events listing header (events sellers only)',
        examples: ['Live music, comedy, and one-of-a-kind performances at Meow Wolf.'],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:events_description',
        'x-requiredFor': ['events'],
        'x-appliesTo': ['events'],
        'x-status': ['cutover-pending'],
      },
      // ── Seller identity (registry) fields — NEW keys, audit convention
      // from birth. The app reads them through getSellers (the landing cards
      // and location pills are built from the sellers), falling back to the
      // SiteKey-keyed values in seller-fallbacks.ts when a key is unset.
      urlSlug: {
        type: 'string',
        description: 'Base URL slug for the seller’s routes; the app appends /admission or /events per seller type',
        examples: ['grapevine'],
        pattern: META_PATTERNS.slug,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:url_slug',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      webEnabled: {
        type: 'boolean',
        description: 'Lists the seller on the web app (landing cards, location pills, venue links); its pages stay reachable by URL either way',
        examples: [true],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:web_enabled',
        // Not required: a required boolean is a no-op (see the schema spec); unset falls back to the site's ENABLED_* config
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      landingPageUrl: {
        type: 'string',
        description: 'The seller’s landing page on the tickets site; the organizer URL in event JSON-LD (events only), omitted when unset',
        examples: ['https://tickets.meowwolf.com/visit/denver'],
        pattern: META_PATTERNS.url,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:landing_page_url',
        'x-appliesTo': ['events'],
      },
      // TODO: DELETE THIS, comes from seller
      // slrTktTimezone: {
      //   type: 'string',
      //   enum: [
      //     'America/New_York',
      //     'America/Chicago',
      //     'America/Denver',
      //     'America/Phoenix',
      //     'America/Los_Angeles',
      //   ],
      //   description: 'IANA timezone of the venue; drives all displayed dates and times',
      //   examples: ['America/Denver'],
      //   'x-source': 'ticketure',
      //   'x-rawKey': 'slr:tkt:timezone',
      //   'x-requiredFor': SELLER_TYPES,
      //   'x-appliesTo': SELLER_TYPES,
      //   'x-status': ['cutover-pending'],
      // },
      // ── Seller branding — NEW keys, audit convention from birth. Replaced
      // the per-seller hardcoding in config/landing-pages.config.ts (card
      // logo/icon/order), the old VenueContext badge icon, the events header
      // SVG map and the old body[data-theme] CSS (header color, events header
      // image). The image fields hold hosted URLs and are the only source:
      // there's no fallback, so a new seller's art is an upload, not a commit.
      logoUrl: {
        type: 'string',
        description: 'Brand logo shown on the venue’s landing-page card; a hosted URL',
        examples: ['https://ticketmanager.meowwolf.com/v1/assets/meowwolf/886e27c9-5977-fcc3-12c3-2f6ab4bf4317'],
        pattern: META_PATTERNS.urlOrPath,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:logo_url',
        'x-requiredFor': ['exhibition'],
        'x-appliesTo': ['exhibition'],
      },
      iconUrl: {
        type: 'string',
        description: 'Venue badge icon on the landing-page card and site badge; a hosted URL',
        examples: ['https://ticketmanager.meowwolf.com/v1/assets/meowwolf/1c52ad3d-53e3-d11a-d2fe-ea3200d3bb9d'],
        pattern: META_PATTERNS.urlOrPath,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:icon_url',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
      },
      // Temporary: the landing page and its site cards are due to be removed
      cardBackgroundUrl: {
        type: 'string',
        description: 'Background image of the venue’s landing-page card; unset shows the card’s gradient only',
        examples: ['https://ticketmanager.meowwolf.com/v1/assets/meowwolf/59c9f25f-a3c7-44d5-4111-ae3dba8c3051?sizex=640'],
        pattern: META_PATTERNS.urlOrPath,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:card_background_url',
        'x-appliesTo': ['exhibition'],
      },
      landingOrder: {
        type: 'number',
        description: 'Position of the venue’s card on the landing page, lowest first; unset goes last, by city',
        examples: [1],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:landing_order',
        'x-appliesTo': ['exhibition'],
        'x-status': ['cutover-pending'],
      },
      eventsHeaderAnimationUrl: {
        type: 'string',
        description: 'Animated neon SVG in the events header (events only); a hosted URL',
        examples: ['https://ticketmanager.meowwolf.com/v1/assets/meowwolf/430f3686-0f47-8e1f-daf3-e16c1fa54e94'],
        pattern: META_PATTERNS.urlOrPath,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:events_header_animation_url',
        'x-appliesTo': ['events'],
      },
      eventsHeaderImageUrl: {
        type: 'string',
        description: 'Background image behind the events header (events only); a hosted URL',
        examples: ['https://ticketmanager.meowwolf.com/v1/assets/meowwolf/b47fb76c-29ce-7cd2-3381-ae78f1323bdd?sizex=1440'],
        pattern: META_PATTERNS.urlOrPath,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:events_header_image_url',
        'x-appliesTo': ['events'],
      },
      headerColor: {
        type: 'string',
        description: 'Header title background color; falls back to black when unset',
        examples: ['#6F2DA8'],
        pattern: META_PATTERNS.hexColor,
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:header_color',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['cutover-pending'],
      },
      portalPassDayPassEnabled: {
        type: 'boolean',
        description: 'Shows the Portal Pass promo to day-pass buyers (true when unset); timed-ticket buyers see it either way',
        examples: [false],
        'x-source': 'ticketure',
        'x-rawKey': 'slr:tkt:portal_pass_day_pass_enabled',
        'x-appliesTo': ['exhibition'],
        'x-status': ['cutover-pending'],
      },
    },
    // EVENT_TEMPLATE_META
    event_template: {
      // Required: getEventSlugFromMeta falls back to '', which routes the
      // event to an empty slug. Required for every seller type — the checklist
      // for this entity runs per event, not per seller (see x-requiredFor)
      urlName: {
        type: 'string',
        description: 'URL slug used to route to the event; must be unique per seller',
        examples: ['convergence-station'],
        'x-source': 'ticketure',
        'x-rawKey': 'url_name',
        'x-proposedKey': 'evt:tkt:url_name',
        'x-requiredFor': SELLER_TYPES,
        'x-appliesTo': SELLER_TYPES,
      },
      eventType: {
        type: 'string',
        enum: ['multi_day'],
        description: 'Alters the purchase flow; multi_day skips single-timeslot behavior',
        examples: ['multi_day'],
        'x-source': 'ticketure',
        'x-rawKey': 'event_type',
        'x-proposedKey': 'evt:tkt:event_type',
        'x-appliesTo': ['events'],
      },
      additionalPricingIds: {
        type: 'string',
        description: 'Comma-separated ticket-type IDs merged into starting-at pricing',
        examples: ['3f7c1d2e-9a45-4b8c-b0d1-6e2f5a9c4b71,8c1e7d3a-2b64-4f09-9a5e-1d7c3b0f6e28'],
        'x-source': 'ticketure',
        'x-rawKey': 'additional_pricing_ids',
        'x-proposedKey': 'evt:tkt:additional_pricing_ids',
        'x-appliesTo': ['exhibition'],
      },
      sessionEndTimeOffset: {
        type: 'string',
        description: 'Offset subtracted from displayed day-pass session end times',
        examples: ['01:00'],
        'x-source': 'ticketure',
        // Named in the doc comment on getSessionEndTimeOffset
        // (data-access/transformers/transformTimeslots.utils.ts)
        'x-rawKey': 'session_end_time_offset',
        'x-proposedKey': 'evt:tkt:session_end_time_offset',
        'x-appliesTo': ['exhibition'],
      },
      // TODO: Metadata tool has to to pull Daypass event (Anytime Tickets) in order to support the day pass button text field
      // dayPassButtonText: {
      //   type: 'string',
      //   description: 'Button label for the day-pass option in timeslot selection',
      //   examples: ['Any time'],
      //   'x-source': 'ticketure',
      //   'x-rawKey': 'day_pass_button_text',
      //   'x-proposedKey': 'evt:tkt:daypass_button_text',
      //   'x-appliesTo': ['exhibition'],
      // },
      // TODO: Metadata tool has to to pull Daypass event (Anytime Tickets) in order to support the day pass button description field
      // dayPassDescription: {
      //   type: 'string',
      //   description: 'Description for the day-pass option in timeslot selection',
      //   examples: ['Enter any time during open hours.'],
      //   'x-source': 'ticketure',
      //   'x-rawKey': 'day_pass_description',
      //   'x-proposedKey': 'evt:tkt:day_pass_description',
      //   'x-appliesTo': ['exhibition'],
      // },
      internalTicketUrlSlug: {
        type: 'string',
        description: 'Reroutes ticket purchase to another internal event page',
        examples: ['convergence-station'],
        'x-source': 'ticketure',
        'x-rawKey': 'internal_ticket_url_slug',
        'x-proposedKey': 'evt:tkt:internal_slug',
        'x-appliesTo': ['events'],
      },
      imageProfile: {
        type: 'string',
        description: 'Event card image URL',
        examples: ['https://cdn.meowwolf.com/events/theta-showcase-card.jpg'],
        'x-source': 'ticketure',
        'x-rawKey': 'image_profile',
        'x-proposedKey': 'evt:tkt:image_profile',
        'x-appliesTo': ['events'],
      },
      imageCover: {
        type: 'string',
        description: 'Event header/cover image URL',
        examples: ['https://cdn.meowwolf.com/events/theta-showcase-cover.jpg'],
        'x-source': 'ticketure',
        'x-rawKey': 'image_cover',
        'x-proposedKey': 'evt:tkt:image_cover',
        'x-appliesTo': ['events'],
      },
      venue: {
        type: 'string',
        description: 'Alternate venue override, format "title|address"; beats venue.meta',
        examples: ['The Moon|1 Orbit Way'],
        'x-source': 'ticketure',
        'x-rawKey': 'venue',
        'x-proposedKey': 'evt:tkt:venue_name_override',
        'x-appliesTo': ['events'],
      },
      tags: {
        type: 'string',
        description: 'Comma-separated tags used for event-card filtering',
        examples: ['rock,pop'],
        'x-source': 'ticketure',
        'x-rawKey': 'tags',
        'x-proposedKey': 'evt:tkt:tags',
        'x-appliesTo': ['events'],
      },
      eventAge: {
        type: 'string',
        description: 'Age restriction copy',
        examples: ['18+'],
        'x-source': 'ticketure',
        'x-rawKey': 'event_age',
        'x-proposedKey': 'evt:tkt:event_age_required',
        'x-appliesTo': ['events'],
      },
      subTitle: {
        type: 'string',
        description: 'Event subtitle',
        examples: ['An evening of experimental sound'],
        'x-source': 'ticketure',
        'x-rawKey': 'sub_title',
        'x-proposedKey': 'evt:tkt:subtitle',
        'x-appliesTo': ['events'],
        'x-status': ['not-read'],
      },
      showStart: {
        type: 'string',
        description: 'Show start time copy (doors vs show)',
        examples: ['Doors 7:00 PM / Show 8:00 PM'],
        'x-source': 'ticketure',
        'x-rawKey': 'show_start',
        'x-proposedKey': 'evt:tkt:show_starttime',
        'x-appliesTo': ['events'],
      },
      eventNote: {
        type: 'string',
        description: 'Note rendered in the event location card',
        examples: ['Parking is available in the garage on Alameda.'],
        'x-source': 'ticketure',
        'x-rawKey': 'event_note',
        'x-proposedKey': 'evt:tkt:event_note',
        'x-appliesTo': ['events'],
      },
      eventNotificationText: {
        type: 'string',
        description: 'Notification banner text on the event page/card',
        examples: ['This event is nearly sold out.'],
        'x-source': 'ticketure',
        'x-rawKey': 'event_notification_text',
        'x-proposedKey': 'evt:tkt:event_notification_text',
        'x-appliesTo': ['events'],
      },
      supportingActs: {
        type: 'string',
        description: 'Supporting acts copy shown on event cards',
        examples: ['with Little Sun and The Hologram Kids'],
        'x-source': 'ticketure',
        'x-rawKey': 'supporting_acts',
        'x-proposedKey': 'evt:tkt:supporting_acts',
        'x-appliesTo': ['events'],
      },
      exhibitionHours: {
        type: 'string',
        description: 'Exhibition hours copy',
        examples: ['Exhibition access included 5:00–9:00 PM'],
        'x-source': 'ticketure',
        'x-rawKey': 'exhibition_hours',
        'x-proposedKey': 'evt:tkt:exhibition_access',
        'x-appliesTo': ['events'],
      },
      notLphEligible: {
        type: 'boolean',
        description: 'Excludes the event from Lifetime Passholder eligibility',
        examples: [true],
        'x-source': 'ticketure',
        'x-rawKey': 'not_lph_eligible',
        'x-proposedKey': 'evt:tkt:not_lph_eligible',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      shortDescription: {
        type: 'string',
        description: 'Short description used on cards/SEO',
        examples: ['A one-night-only performance inside Convergence Station.'],
        'x-source': 'ticketure',
        'x-rawKey': 'short_description',
        'x-proposedKey': 'evt:tkt:short_description',
        'x-appliesTo': ['events'],
        'x-status': ['not-read'],
      },
      externalTicketUrl: {
        type: 'string',
        description: 'External ticketing URL; replaces the internal purchase flow',
        examples: ['https://www.axs.com/events/123456/meow-wolf-tickets'],
        'x-source': 'ticketure',
        'x-rawKey': 'external_ticket_url',
        'x-proposedKey': 'evt:tkt:external_ticket_url',
        'x-appliesTo': ['events'],
      },
      externalBtnTxt: {
        type: 'string',
        description: 'Button label used with externalTicketUrl',
        examples: ['Buy on AXS'],
        'x-source': 'ticketure',
        'x-rawKey': 'external_btn_txt',
        'x-proposedKey': 'evt:tkt:external_btn_txt',
        'x-appliesTo': ['events'],
      },
      seated: {
        type: 'boolean',
        description: 'Marks the event as a seated event',
        examples: [true],
        'x-source': 'ticketure',
        'x-rawKey': 'seated',
        'x-proposedKey': 'evt:tkt:is_seated',
        'x-appliesTo': ['events'],
      },
      mwHeaderDescription: {
        type: 'string',
        description: 'Header description override at the event level',
        examples: ['An evening of experimental sound at Theta Theatre.'],
        'x-source': 'ticketure',
        'x-rawKey': 'mw:header_description',
        'x-proposedKey': 'evt:tkt:mw:header_description',
        'x-appliesTo': ['events'],
        'x-status': ['not-read'],
      },
      // NEW key (audit-convention from birth, no x-proposedKey). Replaces the
      // site-config PREVIEW_CONFIG.EXHIBITION_ADMISSIONS_EVENT_ID comparison:
      // instead of matching a configured event id, isPreviewEvent will read
      // this flag off the event's own meta. Read-path cutover pending.
      evtTktIsPreviewEvent: {
        type: 'boolean',
        description: 'Marks the preview/pre-opening admissions event (gates preview-session behavior)',
        examples: [true],
        'x-source': 'ticketure',
        'x-rawKey': 'evt:tkt:is_preview_event',
        'x-appliesTo': ['exhibition'],
        'x-status': ['not-read'],
      },
    },
    // TICKET_GROUP_META
    ticket_group: {
      // TODO: unused by Ticketing Ops (confirmed with Michelle 8/5/26) remove this
      // https://app.asana.com/1/23646394350278/project/1206566497440920/task/1217199622292463
      mwtixHideTicketGroup: {
        type: 'boolean',
        description: 'Hides the ticket group from sale in the webapp',
        examples: [true],
        'x-source': 'ticketure',
        'x-rawKey': 'mwtix:hide_ticket_group',
        'x-proposedKey': 'tg:tkt:hide_ticket_group',
        'x-appliesTo': SELLER_TYPES,
      },
      local: {
        type: 'boolean',
        description: 'Marks a local-resident category; gates local-pricing UI',
        examples: [true],
        'x-source': 'ticketure',
        'x-rawKey': 'local',
        'x-proposedKey': 'tg:tkt:is_local_ticket',
        'x-appliesTo': ['exhibition'],
      },
      addons: {
        type: 'boolean',
        description: 'Category is an add-on; excluded from main tiers and sorted into the add-ons section',
        examples: [true],
        'x-source': 'ticketure',
        'x-rawKey': 'addons',
        'x-proposedKey': 'tg:tkt:addons',
        'x-appliesTo': ['events'],
      },
      // Was 'lifeTimeTickets', which camelCases to 'lifeTimeTickets' — but the
      // app reads META_LIFETIME_TICKETS ('lifetimeTickets'), so that raw key
      // could never have matched on read. Corrected to the form that satisfies
      // camelCase(rawKey) === schema key, which is what the read path requires.
      lifetimeTickets: {
        type: 'boolean',
        description: 'Category bypasses per-membership quantity limits',
        examples: [true],
        'x-source': 'ticketure',
        'x-rawKey': 'lifetime_tickets',
        'x-proposedKey': 'tg:tkt:is_lifetimetickets',
        'x-appliesTo': SELLER_TYPES,
      },
    },
    // TICKET_TYPE_META
    ticket_type: {
      image: {
        type: 'string',
        description: 'Tier image URL',
        examples: ['https://cdn.meowwolf.com/tiers/general-admission.jpg'],
        'x-source': 'ticketure',
        'x-rawKey': 'image',
        'x-proposedKey': 'tt:tkt:image_url',
        'x-appliesTo': SELLER_TYPES,
      },
      imageBgColor: {
        type: 'string',
        description: 'Background color behind the tier image',
        examples: ['#FF0000'],
        'x-source': 'ticketure',
        'x-rawKey': 'image_bg_color',
        'x-proposedKey': 'tt:tkt:image_bg_color',
        'x-appliesTo': SELLER_TYPES,
      },
      // NEW key (audit-convention from birth, no x-proposedKey). Replaces the
      // site-config MAX_TICKETS_FOR_TIER array (SiteEnv), which mapped a
      // ticket-type id to a per-order cap and is read at TiersForCategory.tsx.
      // Modeled here on the ticket_type itself rather than as a seller-level
      // number so the cap travels with the tier — a single seller number could
      // not express "this tier capped, others uncapped" (what PREVIEW does).
      // Unset means no per-tier cap. Read onto Transformed.TicketTier.maxPerOrder;
      // TiersForCategory falls back to the PREVIEW-only array until prod carries it.
      ttTktMaxTicketsForOrder: {
        type: 'number',
        description: 'Maximum quantity of this tier allowed per order; unset means no per-tier cap',
        examples: [10],
        'x-source': 'ticketure',
        'x-rawKey': 'tt:tkt:max_tickets_for_order',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
    },
    // ── App-wide feature flags (Vercel Edge Config) — everything below
    // affects the whole deployment, not a single Ticketure resource ──
    // we use two edge configs to handle different environments (production vs preview), but the schema is the same for both.
    // production feature flags apply to staging
    // preview feature flags apply to preview and development
    'feature-flags': {
      enabledPerks: {
        type: 'boolean',
        description: 'Enables perk redemption in the cart',
        examples: [true],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      enabledGtm: {
        type: 'boolean',
        description: 'Enables Google Tag Manager tracking (with gtmId)',
        examples: [true],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      enabledSegment: {
        type: 'boolean',
        description: 'Enables Segment tracking (with segmentKey)',
        examples: [true],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      // ── Flags currently hardcoded in config/*.config.ts, so flipping one
      // needs a deploy. Modeled here so they become Edge Config toggles.
      // Read-path cutover pending (each still reads its config constant).
      calendarTooltipsEnabled: {
        type: 'boolean',
        description: 'Enables tooltips on calendar days (config/environments.config CALENDAR_TOOLTIPS_ENABLED)',
        examples: [false],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      timeslotPricingEnabled: {
        type: 'boolean',
        description: 'Enables per-timeslot pricing display (config/environments.config TIMESLOT_PRICING_ENABLED)',
        examples: [true],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      lockingEnabled: {
        type: 'boolean',
        description: 'Enables the lock screen / access gate (config/environments.config LOCKING_ENABLED). Should only be enabled in non-prod environments.',
        examples: [true],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      advancedFilteringEnabled: {
        type: 'boolean',
        description: 'Enables advanced event filtering UI (config/events.config ADVANCED_FILTERING_ENABLED)',
        examples: [false],
        'x-source': 'vercel',
        'x-appliesTo': ['events'],
        'x-status': ['not-read'],
      },
      enableTrackingInDevEnv: {
        type: 'boolean',
        description: 'Allows analytics tracking to fire in development environments (config/flags.config ENABLE_TRACKING_IN_DEV_ENV)',
        examples: [false],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      zarazConsentEnabled: {
        type: 'boolean',
        description: 'Enables the Zaraz consent banner (env NEXT_PUBLIC_ZARAZ_CONSENT_ENABLED)',
        examples: [true],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
    },
    // APP wide configuration variables
    'app-config': {
      providerApiUrl: {
        type: 'string',
        description: 'Ticketure provider base URL; also builds print/manage links',
        examples: ['https://ticketmanager.meowwolf.com'],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      auth0Domain: {
        type: 'string',
        description: 'Auth0 tenant domain (public half; secrets stay in env vars)',
        examples: ['auth.meowwolf.com'],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      auth0ClientId: {
        type: 'string',
        description: 'Auth0 application client id (public half; secrets stay in env vars)',
        examples: ['pQ7xK2mR9tVw4LzB1nY6cJ8dF3hG5sA0'],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      giftEmailTemplateId: {
        type: 'string',
        description: 'Email template id used for gifted orders',
        examples: ['d-4f8c1b2a9d3e4c07b0d16e2f5a9c4b71'],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      segmentKey: {
        type: 'string',
        description: 'Segment write key; gates USE_SEGMENT together with enabledSegment',
        examples: ['kL3vN8pQ2rT7wX1yZ6bC4mH9dG5fJ0sA'],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      gtmId: {
        type: 'string',
        description: 'Google Tag Manager container id; gates USE_GTM together with enabledGtm',
        examples: ['GTM-PM8PC3L'],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      // ── Numeric tunables hardcoded in config/*.config.ts today, so a change
      // needs a deploy. Modeled here as Edge Config values. Read-path cutover
      // pending (each still reads its config constant).
      ticketsRemainingThreshold: {
        type: 'number',
        description: 'Remaining-ticket count below which the low-stock message shows (config/environments.config TICKETS_REMAINING_THRESHOLD)',
        examples: [20],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      lockExpiration: {
        type: 'number',
        description: 'Lock-screen token lifetime in seconds (config/environments.config LOCK_EXPIRATION)',
        examples: [7200],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      descriptionCharLimit: {
        type: 'number',
        description: 'Character limit before an event description is truncated (config/events.config DESCRIPTION_CHAR_LIMIT)',
        examples: [500],
        'x-source': 'vercel',
        'x-appliesTo': ['events'],
        'x-status': ['not-read'],
      },
      // ── Non-secret infra values still read from env. True secrets
      // (AUTH0_CLIENT_SECRET, TICKETURE_AUTH_TOKEN, AIRTABLE_API_KEY,
      // SECURITY_KEY_SECRET) stay in env vars and are deliberately NOT modeled.
      ticketureApiBaseUrl: {
        type: 'string',
        description: 'Ticketure API base URL used for unproxied requests (env TICKETURE_API_BASE_URL)',
        examples: ['https://meowwolf.web.d1tix.com'],
        pattern: META_PATTERNS.url,
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      airtableApiUrl: {
        type: 'string',
        description: 'Airtable API base URL for notifications/calendar metadata (env AIRTABLE_API_URL)',
        examples: ['https://api.airtable.com/v0/appXXXXXXXXXXXXXX'],
        pattern: META_PATTERNS.url,
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      stripePublishableKey: {
        type: 'string',
        description: 'Stripe publishable key (public half; secret key stays in env). config/payment.config STRIPE_KEY',
        examples: ['pk_live_51H8xK2mR9tVw4LzB1nY6cJ8dF3hG5sA0'],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
      stripeDefaultAccount: {
        type: 'string',
        description: 'Default Stripe connected-account id (config/payment.config DEFAULT_STRIPE_ACCOUNT)',
        examples: ['acct_1H8xK2mR9tVw4LzB'],
        'x-source': 'vercel',
        'x-appliesTo': SELLER_TYPES,
        'x-status': ['not-read'],
      },
    },
  },
} as const satisfies SchemasShape

// ---------------------------------------------------------------------------
// Flat view — one entry per field, for the MetadataManagementTool UI
// ---------------------------------------------------------------------------

export type FlatMetaFieldSchema = MetaFieldBase & {
  'x-source': MetaSource
  'x-rawKey'?: string
  key: string
  resourceType: MetaEntityKind
}

/**
 * Flatten METADATA_SCHEMAS.global into a list the UI can map over. The same
 * list serves every seller — see the note on `global`.
 *
 * @param {Partial<Record<MetaEntityKind, Record<string, MetaFieldSchema>>>} schemas - entity → fields map
 * @returns {FlatMetaFieldSchema[]} one entry per field, stamped with key and resourceType
 */
export const flattenSchemas = (
  schemas: Partial<Record<MetaEntityKind, Record<string, MetaFieldSchema>>>,
): FlatMetaFieldSchema[] => (
  Object.entries(schemas).flatMap(([resourceType, fields]) => (
    Object.entries(fields).map(([key, field]) => ({
      ...field,
      key,
      resourceType: resourceType as MetaEntityKind,
    }))
  ))
)

// ---------------------------------------------------------------------------
// Derived types — parsed (post-coercion) value shapes via json-schema-to-ts
// ---------------------------------------------------------------------------

type MetaShape<S> = {
  [K in keyof S]?: S[K] extends JSONSchema ? FromSchema<S[K]> : never
}

type GlobalSchemas = typeof METADATA_SCHEMAS['global']

/**
 * Parsed metadata shape, per entity kind. There is one shape and every seller
 * has it: no seller carries a field another seller lacks, so there is no
 * `SellerMetadata<'KTX'>` to parameterise. Fields a given seller does not use
 * are simply unset — which is why every property here is optional.
 */
export type GlobalMetadata = {
  [E in MetaEntityKind]: MetaShape<GlobalSchemas[E]>
}
